import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from '../../hooks/useTranslation';
import './FileUploadModal.css';

interface FileUploadModalProps {
  onClose: () => void;
  onUpload: (file: File) => void;
  loading: boolean;
  accept?: string;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({ 
  onClose, 
  onUpload, 
  loading, 
  accept 
}) => {
  const { t } = useTranslation();
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      onUpload(acceptedFiles[0]);
    }
  }, [onUpload]);

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    onDrop,
    accept: accept ? {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    } : undefined,
    multiple: false
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content file-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('fileUpload.title')}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? 'drag-active' : ''}`}
          >
            <input {...getInputProps()} />
            <div className="dropzone-content">
              {isDragActive ? (
                <p className="dropzone-text">{t('fileUpload.dragActive')}</p>
              ) : (
                <>
                  <div className="dropzone-icon">📁</div>
                  <p className="dropzone-text">
                    {t('fileUpload.dragInactive')}
                  </p>
                  <p className="dropzone-hint">{t('fileUpload.accepts')}: {accept || '.xlsx, .xls'}</p>
                </>
              )}
            </div>
          </div>

          {acceptedFiles.length > 0 && (
            <div className="selected-file">
              <strong>{t('fileUpload.selectedFile')}:</strong> {acceptedFiles[0].name}
            </div>
          )}

          {loading && (
            <div className="upload-loading">
              {t('fileUpload.uploading')}
            </div>
          )}

          <div className="modal-actions">
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={loading}
            >
              {t('fileUpload.cancel')}
            </button>
            {acceptedFiles.length > 0 && (
              <button
                className="upload-button"
                onClick={() => onUpload(acceptedFiles[0])}
                disabled={loading}
              >
                {loading ? t('fileUpload.uploading') : t('fileUpload.upload')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadModal;

